import {
  istEnglischHersteller,
  istJabHersteller,
  istSarlasHersteller,
  type AktualisiereStoffInput,
  type JabStatus,
  type StoffIntern,
} from "@repo/shared";

import { api, errorMessage } from "./api";

/** Lieferanten, deren Farben die Stoffmaske über ein Händlerportal abgleicht; „englisch“ = Englisch Dekor (ED). */
export type Lieferant = "jab" | "sarlas" | "englisch";

/**
 * Abgeglichene Farben eines Stoffs.
 * `bilder`: Farbnummer → Bildadresse; null = nie mit Bildern abgeglichen, {} = abgeglichen, aber keine Bilder.
 * `namen`: Farbnummer → Farbname (nur Sarlas liefert Namen; JAB und ED nicht).
 */
export type FarbStand = { farben: string[]; bilder: Record<string, string> | null; namen: Record<string, string> };

/** Verfügbarkeit einer Farbe; `erwartet` ist ein angekündigter Zugang (nur Sarlas). */
export type FarbVerfuegbarkeit = {
  meter: number | null;
  status: JabStatus;
  erwartet: { meter: number; termin: string | null } | null;
};

export type FarbabgleichAdapter = {
  /** Beschriftung des Abgleich-Knopfs. */
  knopf: string;
  /** Der am Stoff gespeicherte Stand. */
  ausStoff(s: StoffIntern): FarbStand;
  /**
   * Gleicht die Farben ab; mit `stoffId` speichert der Server sie sofort am Stoff.
   * Wirft einen Error mit Klartext. `vorschlagNummer`: Stoffnummer, die übernommen werden sollte (JAB: immer null).
   * `breiteMm`: Ballenbreite als positive ganze Zahl (siehe `abgleichBreite`); nur ED nutzt sie.
   */
  abgleichen(e: {
    herstellerSchluessel: string;
    name: string;
    nummer: string;
    breiteMm?: number;
    stoffId?: string;
  }): Promise<FarbStand & { treffer: string; gespeichert: boolean; vorschlagNummer: string | null }>;
  /** Hinweis unter „Stoffnummer“; null = keiner. */
  nummerHinweis: string | null;
  /** Beschriftung des Knopfs, der die vorgeschlagene Stoffnummer `nr` übernimmt. */
  vorschlagText(nr: string): string;
  /**
   * Verfügbarkeit der Farbe; wirft einen Error mit Klartext. JAB ermittelt einen fehlenden Treffer selbst
   * und meldet ihn sofort über `trefferErmittelt` – auch wenn die Verfügbarkeit danach scheitert.
   */
  verfuegbarkeit(e: {
    herstellerSchluessel: string;
    name: string;
    nummer: string;
    treffer: string | null;
    farbnummer: string;
    trefferErmittelt?(treffer: string): void;
  }): Promise<FarbVerfuegbarkeit>;
  /** Felder für „Speichern“; Bilder und Namen nur zusammen mit den Farben, den Zeitpunkt setzt der Server. */
  speicherFelder(f: FarbStand): Partial<AktualisiereStoffInput>;
};

/** Name des Lieferanten in Texten, z. B. „nicht bei JAB“. */
export const LIEFERANT_NAME: Record<Lieferant, string> = { jab: "JAB", sarlas: "Sarlas", englisch: "ED" };

export function lieferantVon(herstellerSchluessel: string): Lieferant | null {
  if (istJabHersteller(herstellerSchluessel)) return "jab";
  if (istSarlasHersteller(herstellerSchluessel)) return "sarlas";
  if (istEnglischHersteller(herstellerSchluessel)) return "englisch";
  return null;
}

/**
 * Ballenbreite für den ED-Abgleich: nur eine positive ganze Zahl, sonst undefined – dann entfällt das Feld
 * in der Anfrage. ED führt dieselbe Farbe je Breite als eigene Produktgruppe.
 */
export function abgleichBreite(mm: number | null | undefined): number | undefined {
  return typeof mm === "number" && Number.isSafeInteger(mm) && mm > 0 ? mm : undefined;
}

const ABGLEICH_FEHLER = "Abgleich fehlgeschlagen";
const VERFUEGBARKEIT_FEHLER = "Verfügbarkeit gerade nicht abrufbar";

const jab: FarbabgleichAdapter = {
  knopf: "Jabfarben abgleichen",
  nummerHinweis: null,
  // Ungenutzt: JAB schlägt keine Stoffnummer vor (vorschlagNummer ist immer null).
  vorschlagText: (nr) => `${nr} als Stoffnummer übernehmen`,

  ausStoff: (s) => ({ farben: s.jabFarben ?? [], bilder: s.jabFarbbilder, namen: {} }),

  async abgleichen({ herstellerSchluessel, name, nummer, stoffId }) {
    const res = await api.api.admin.jab.farben.$post({ json: { herstellerSchluessel, name, nummer, stoffId } });
    if (!res.ok) throw new Error(await errorMessage(res, ABGLEICH_FEHLER));
    const d = await res.json();
    if (!("farben" in d)) throw new Error(ABGLEICH_FEHLER);
    return { farben: d.farben, bilder: d.bilder, namen: {}, treffer: d.treffer, gespeichert: d.gespeichert, vorschlagNummer: null };
  },

  async verfuegbarkeit({ herstellerSchluessel, name, nummer, treffer, farbnummer, trefferErmittelt }) {
    let gesucht = treffer;
    if (!gesucht) {
      // Gespeicherte Farben ohne Treffer aus dieser Sitzung: Treffer erst neu ermitteln.
      const r = await api.api.admin.jab.farben.$post({ json: { herstellerSchluessel, name, nummer } });
      if (!r.ok) throw new Error(await errorMessage(r, ABGLEICH_FEHLER));
      const dd = await r.json();
      if (!("treffer" in dd)) throw new Error(ABGLEICH_FEHLER);
      gesucht = dd.treffer;
      trefferErmittelt?.(gesucht);
    }
    const res = await api.api.admin.jab.verfuegbarkeit.$post({ json: { herstellerSchluessel, treffer: gesucht, farbnummer } });
    if (res.status === 502) throw new Error(VERFUEGBARKEIT_FEHLER);
    if (!res.ok) throw new Error(await errorMessage(res, VERFUEGBARKEIT_FEHLER));
    const d = await res.json();
    if (!("meter" in d)) throw new Error(VERFUEGBARKEIT_FEHLER);
    return { meter: d.meter, status: d.status, erwartet: null };
  },

  speicherFelder: (f) => ({ jabFarben: f.farben, ...(f.bilder ? { jabFarbbilder: f.bilder } : {}) }),
};

const sarlas: FarbabgleichAdapter = {
  knopf: "Sarlasfarben abgleichen",
  nummerHinweis: "Tirella-Nr. des Musterhängers (z. B. 2525)",
  vorschlagText: (nr) => `Tirella ${nr} als Stoffnummer übernehmen`,

  ausStoff: (s) => ({ farben: s.sarlasFarben ?? [], bilder: s.sarlasFarbbilder, namen: s.sarlasFarbnamen ?? {} }),

  async abgleichen({ herstellerSchluessel, name, nummer, stoffId }) {
    const res = await api.api.admin.sarlas.farben.$post({ json: { herstellerSchluessel, name, nummer, stoffId } });
    // Unklare Zuordnung (404) oder fehlende Tirella-Nr. (400) kommen als Klartext und werden so gezeigt.
    if (!res.ok) throw new Error(await errorMessage(res, ABGLEICH_FEHLER));
    const d = await res.json();
    if (!("farben" in d)) throw new Error(ABGLEICH_FEHLER);
    return {
      farben: d.farben,
      bilder: d.bilder,
      namen: d.namen,
      treffer: d.treffer,
      gespeichert: d.gespeichert,
      vorschlagNummer: d.vorschlag,
    };
  },

  // Sarlas braucht keinen Treffer – die Design-Nr. der Farbe genügt.
  async verfuegbarkeit({ herstellerSchluessel, farbnummer }) {
    const res = await api.api.admin.sarlas.verfuegbarkeit.$post({ json: { herstellerSchluessel, farbnummer } });
    if (res.status === 502) throw new Error(VERFUEGBARKEIT_FEHLER);
    if (!res.ok) throw new Error(await errorMessage(res, VERFUEGBARKEIT_FEHLER));
    const d = await res.json();
    if (!("meter" in d)) throw new Error(VERFUEGBARKEIT_FEHLER);
    return { meter: d.meter, status: d.status, erwartet: d.erwartet };
  },

  speicherFelder: (f) => ({
    sarlasFarben: f.farben,
    ...(f.bilder ? { sarlasFarbbilder: f.bilder } : {}),
    sarlasFarbnamen: f.namen,
  }),
};

/** Englisch Dekor („ED“): Stoffnummer = Produktcode, Farbnummer = voller Artikelcode; keine Farbnamen. */
const englisch: FarbabgleichAdapter = {
  knopf: "Farbabgleich ED",
  nummerHinweis: "ED-Produktcode, z. B. E3710-E3723/150 – mehrere Produkte mit „+“",
  vorschlagText: (nr) => `Produktcode ${nr} als Stoffnummer übernehmen`,

  ausStoff: (s) => ({ farben: s.englischFarben ?? [], bilder: s.englischFarbbilder, namen: {} }),

  async abgleichen({ herstellerSchluessel, name, nummer, breiteMm, stoffId }) {
    // Die Ballenbreite geht nur als positive ganze Zahl mit, sonst entfällt das Feld.
    const breite = abgleichBreite(breiteMm);
    const res = await api.api.admin.englisch.farben.$post({
      json: { herstellerSchluessel, name, nummer, ...(breite !== undefined ? { breiteMm: breite } : {}), stoffId },
    });
    // Unklare Zuordnung (404) oder fehlender Produktcode (400) kommen als Klartext und werden so gezeigt.
    if (!res.ok) throw new Error(await errorMessage(res, ABGLEICH_FEHLER));
    const d = await res.json();
    if (!("farben" in d)) throw new Error(ABGLEICH_FEHLER);
    return {
      farben: d.farben,
      bilder: d.bilder,
      namen: {},
      treffer: d.treffer,
      gespeichert: d.gespeichert,
      vorschlagNummer: d.vorschlag,
    };
  },

  // ED braucht keinen Treffer – der volle Artikelcode der Farbe genügt.
  async verfuegbarkeit({ herstellerSchluessel, farbnummer }) {
    const res = await api.api.admin.englisch.verfuegbarkeit.$post({ json: { herstellerSchluessel, farbnummer } });
    if (res.status === 502) throw new Error(VERFUEGBARKEIT_FEHLER);
    if (!res.ok) throw new Error(await errorMessage(res, VERFUEGBARKEIT_FEHLER));
    const d = await res.json();
    if (!("meter" in d)) throw new Error(VERFUEGBARKEIT_FEHLER);
    return { meter: d.meter, status: d.status, erwartet: null };
  },

  // Farben und Bilder nur gemeinsam; nach einem Abgleich sind die Bilder nie null.
  speicherFelder: (f) => ({ englischFarben: f.farben, ...(f.bilder ? { englischFarbbilder: f.bilder } : {}) }),
};

/** Farbabgleich je Lieferant – die Stoffmaske bedient JAB, Sarlas und ED gleich. */
export const FARBABGLEICH: Record<Lieferant, FarbabgleichAdapter> = { jab, sarlas, englisch };
